import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';

import {
  forgotPasswordDefaultValues,
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from '@/lib/auth-schemas';
import { getApiErrorMessage } from '@/lib/api-error';
import { useForgotPasswordMutation } from '@/query/auth';
import '@/styles/route-auth.scss';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const navigate = useNavigate();
  const mutation = useForgotPasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: forgotPasswordDefaultValues,
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    if (mutation.isPending) return;
    const email = data.email.trim().toLowerCase();
    try {
      await mutation.mutateAsync({ email });
      await navigate({
        to: '/reset-password',
        search: { email },
      });
    } catch {
      // Error surfaced via mutation state below.
    }
  };

  const errorMessage = mutation.isError
    ? getApiErrorMessage(
        mutation.error,
        'Could not send the reset email. Please try again.',
      )
    : null;

  return (
    <main className="auth">
      <div className="auth__brand">World Athletics · Book of Rules</div>

      <div className="auth__card">
        <h1 className="auth__heading">Forgot password?</h1>
        <p className="auth__subtitle">
          Enter your email address and we&apos;ll send you a verification code
          to reset your password.
        </p>

        <form
          className="auth__form"
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <div
            className={`auth__field${errors.email ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="forgot-email"
              className="auth__field-input auth__field-input--no-toggle"
              type="email"
              placeholder=" "
              autoComplete="email"
              {...register('email')}
            />
            <label className="auth__field-label" htmlFor="forgot-email">
              Email
            </label>
            {errors.email && (
              <p className="auth__field-error">{errors.email.message}</p>
            )}
          </div>

          {errorMessage && (
            <div role="alert" className="auth__error">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="auth__submit"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <span className="auth__submit-spinner" aria-hidden="true" />
                Sending…
              </>
            ) : (
              'Send reset code'
            )}
          </button>
        </form>

        <Link to="/login" className="auth__back-link">
          Back to sign in
        </Link>
      </div>

      <div className="auth__footer">© World Athletics</div>
    </main>
  );
}
